
import { useState, useRef, useEffect } from 'react';

interface HumanFactorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function HumanFactorModal({ isOpen, onClose }: HumanFactorModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      setError('');
    }
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const company = formData.get('company') as string;
    const role = formData.get('role') as string;
    const message = formData.get('message') as string;

    if (!name.trim() || !email.trim() || !company.trim()) {
      setError('Name, email, and company are required.');
      return;
    }

    if (message && message.length > 500) {
      setError('Message must be 500 characters or fewer.');
      return;
    }

    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('name', name.trim());
      body.append('email', email.trim());
      body.append('company', company.trim());
      if (role.trim()) body.append('role', role.trim());
      if (message.trim()) body.append('message', message.trim());

      await fetch('https://readdy.ai/api/form/d69umo5bd17lru29lg30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
    >
      <div className="bg-stone-900 text-stone-50 w-full max-w-md p-10 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-50 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {submitted ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 text-red-500">
              <i className="ri-check-line text-3xl"></i>
            </div>
            <h3 className="text-2xl font-serif mb-3">Received.</h3>
            <p className="text-stone-300 leading-relaxed">
              We\u2019ll review your inquiry and<br />respond within one business day.
            </p>
            <button
              onClick={onClose}
              className="mt-8 px-8 py-3 border border-stone-50 text-stone-50 text-sm font-medium hover:bg-stone-50 hover:text-stone-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-2xl font-serif mb-2">Activate HumanFactor</h3>
            <p className="text-sm text-stone-400 mb-8">Private Operating Retainer. $999/build.</p>

            <form
              ref={formRef}
              id="humanfactor-inquiry-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-2">Full Name</label>
                <input
                  type="text"
                  name="name"
                  className="w-full border border-stone-600 bg-stone-800 px-4 py-3 text-sm text-stone-50 focus:outline-none focus:border-stone-50 transition-colors placeholder-stone-500"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  className="w-full border border-stone-600 bg-stone-800 px-4 py-3 text-sm text-stone-50 focus:outline-none focus:border-stone-50 transition-colors placeholder-stone-500"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-2">Company</label>
                <input
                  type="text"
                  name="company"
                  className="w-full border border-stone-600 bg-stone-800 px-4 py-3 text-sm text-stone-50 focus:outline-none focus:border-stone-50 transition-colors placeholder-stone-500"
                  placeholder="Your organization"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-2">Role <span className="text-stone-500">(optional)</span></label>
                <input
                  type="text"
                  name="role"
                  className="w-full border border-stone-600 bg-stone-800 px-4 py-3 text-sm text-stone-50 focus:outline-none focus:border-stone-50 transition-colors placeholder-stone-500"
                  placeholder="e.g. Operations Director"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-2">What are you looking to govern? <span className="text-stone-500">(optional)</span></label>
                <textarea
                  name="message"
                  maxLength={500}
                  rows={3}
                  className="w-full border border-stone-600 bg-stone-800 px-4 py-3 text-sm text-stone-50 focus:outline-none focus:border-stone-50 transition-colors resize-none placeholder-stone-500"
                  placeholder="Brief description of your automation needs"
                ></textarea>
              </div>

              {error && <p className="text-red-400 text-sm">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 border-2 border-stone-50 text-stone-50 text-sm font-medium hover:bg-stone-50 hover:text-stone-900 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {submitting ? 'Submitting\u2026' : 'Activate HumanFactor'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
